import { Injectable, computed, signal } from '@angular/core';

export interface CopilotContextChip {
  id: string;
  sourceType: string;
  sourceId: string;
  sourceKey: string;
  label: string;
  /** Optional payload text that is appended to the final message body on send. */
  fullText?: string;
  capturedAt: string;
}

export interface AddCopilotContextInput {
  sourceType: string;
  sourceId: string;
  label: string;
  /**
   * Optional context payload used during final prompt assembly.
   * For demo-only file/image chips this may be omitted.
   */
  fullText?: string;
}

export type AddContextFailureReason = 'duplicate' | 'max_contexts';

export interface AddContextResult {
  added: boolean;
  reason?: AddContextFailureReason;
}

@Injectable({
  providedIn: 'root',
})
export class CopilotContextComposerService {
  readonly maxContextItems = 25;

  private readonly _draftText = signal('');
  private readonly _chips = signal<CopilotContextChip[]>([]);

  readonly draftText = this._draftText.asReadonly();
  readonly chips = this._chips.asReadonly();

  readonly sendableContextLines = computed(() =>
    this._chips()
      .map((chip) => (chip.fullText ?? '').trim())
      .filter((line) => line.length > 0)
  );

  /**
   * Final message format:
   * 1) user text
   * 2) empty line
   * 3) bold context header (markdown-safe)
   * 4) one numbered context entry per line
   */
  readonly compiledMessageText = computed(() => {
    const draft = this._draftText().trim();
    const contextLines = this.sendableContextLines();
    const contextBlock =
      contextLines.length > 0
        ? `**ADDED INPUTS**\n${contextLines
            .map((line, idx) => `[${idx + 1}] ${line}`)
            .join('\n\n')}`
        : '';

    if (draft.length > 0 && contextBlock.length > 0) {
      return `${draft}\n\n${contextBlock}`;
    }

    if (draft.length > 0) {
      return draft;
    }

    if (contextBlock.length > 0) {
      return contextBlock;
    }

    return '';
  });

  /** Any user-visible composer state (draft text and/or attached chips). */
  readonly hasContent = computed(
    () => this._draftText().trim().length > 0 || this._chips().length > 0
  );

  /** Content that can actually be sent to backend. */
  readonly hasSendableContent = computed(
    () => this.compiledMessageText().trim().length > 0
  );

  setDraftText(text: string): void {
    this._draftText.set(text);
  }

  addContext(input: AddCopilotContextInput): AddContextResult {
    const sourceKey = `${input.sourceType}:${input.sourceId}`;

    if (this._chips().some((chip) => chip.sourceKey === sourceKey)) {
      return { added: false, reason: 'duplicate' };
    }

    if (this._chips().length >= this.maxContextItems) {
      return { added: false, reason: 'max_contexts' };
    }

    const chip: CopilotContextChip = {
      id: this._newId('chip'),
      sourceType: input.sourceType,
      sourceId: input.sourceId,
      sourceKey,
      label: input.label,
      fullText: input.fullText,
      capturedAt: new Date().toISOString(),
    };

    this._chips.update((chips) => [...chips, chip]);
    return { added: true };
  }

  removeContext(chipId: string): void {
    this._chips.update((chips) => chips.filter((chip) => chip.id !== chipId));
  }

  clearContexts(): void {
    this._chips.set([]);
  }

  clear(): void {
    this._draftText.set('');
    this._chips.set([]);
  }

  private _newId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}
